import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt, faCar, faCoins, faUser, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';

interface Props { access: any; }

const EXP_STATUS: Record<string, { cls: string; key: string }> = {
  pending: { cls: 'pending', key: 'Pending' },
  rejected: { cls: 'rejected', key: 'Rejected' },
  fulfilled: { cls: 'fulfilled', key: 'Approved' },
  paid: { cls: 'paid', key: 'Paid' },
};


const initials = (a?: string, b?: string) => `${(a || '')[0] || ''}${(b || '')[0] || ''}`.toUpperCase() || '—';
const money = (n: any) => (Number(n) || 0).toFixed(2);

const MobileFinance: React.FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Record<string, boolean>>({ expenses: true, income: true });
  const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const [expenses, setExpenses] = useState<any[]>([]);
  const [income, setIncome] = useState<any[]>([]);
  const [car, setCar] = useState<any[]>([]);
  const [payroll, setPayroll] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const rq = {
      startTime: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
      endTime: dayjs().format('YYYY-MM-DD'),
    };
    const [expRes, incRes, carRes, payRes] = await Promise.allSettled([
      axios.post('expenses', rq),
      axios.post('finance-income', rq),
      axios.post('cars-expenses', rq),
      axios.get('users/finance'),
    ]);

    const exp = expRes.status === 'fulfilled' && Array.isArray(expRes.value.data?.expenses) ? expRes.value.data.expenses : [];
    const inc = incRes.status === 'fulfilled' && Array.isArray(incRes.value.data?.income) ? incRes.value.data.income : [];
    const crx = carRes.status === 'fulfilled' && Array.isArray(carRes.value.data) ? carRes.value.data : [];
    const pay = payRes.status === 'fulfilled' && Array.isArray(payRes.value.data) ? payRes.value.data : [];

    setExpenses(exp);
    setIncome(inc);
    setCar(crx);
    setPayroll(pay);
    setLoading(false);
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const sumBy = (arr: any[], field: string) => arr.filter((r) => r.status !== 'rejected').reduce((s, r) => s + (Number(r[field]) || 0), 0);

  const stats = useMemo(() => {
    const incoming = sumBy(income, 'amount');
    const outgoing = sumBy(expenses, 'amount') + sumBy(car, 'cost');
    return {
      balance: incoming - outgoing,
      incoming,
      outgoing,
      transactions: expenses.length + income.length + car.length,
    };
  }, [expenses, income, car]);

  if (loading) return <LoadingCenter />;

  const creator = (r: any) => (r.create_user ? `${r.create_user.first_name ?? ''} ${r.create_user.last_name ?? ''}`.trim() : '');

  const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
    const st = EXP_STATUS[status || ''] || EXP_STATUS.pending;
    return <span className={`sbadge ${st.cls}`}>{t(st.key)}</span>;
  };

  // строка транзакции внутри раскрытой секции
  const txRow = (e: any, color: string, sign: '+' | '-', amountField: string, titleField = 'name') => (
    <div key={e.id} className="m-acc-row">
      <span className="m-li-main">
        <span className="m-li-title">{e[titleField] || e.description || e.purpose || e.name || '—'}</span>
        <span className="m-li-sub">{[e.date ? dayjs(e.date).format('D MMM') : '', e.categoryTree, creator(e)].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="m-li-right">
        <span className="m-li-amt" style={{ color }}>{sign}{money(e[amountField])}</span>
        <div style={{ marginTop: 4 }}><StatusBadge status={e.status} /></div>
      </span>
    </div>
  );

  const payRow = (u: any) => (
    <div key={u.id} className="m-acc-row">
      <span className="m-av" style={{ width: 36, height: 36, fontSize: 13, flexShrink: 0 }}>{initials(u.first_name, u.last_name)}</span>
      <span className="m-li-main">
        <span className="m-li-title">{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—'}</span>
        <span className="m-li-sub">{[u.pattern?.name, u.pattern?.hoursPerWeek != null ? `${u.pattern.hoursPerWeek} ${t('PerWeek')}` : ''].filter(Boolean).join(' · ') || '—'}</span>
      </span>
      <span className="m-li-right">
        <span className="m-li-amt">{money(u.salary_perHour)}</span>
        <div className="m-li-sub" style={{ marginTop: 2 }}>{t('CostPerHour')}</div>
      </span>
    </div>
  );

  interface SectionDef {
    key: string; label: string; icon: any; color: string; bg: string;
    count: number; totalLabel: string; totalNode: React.ReactNode; body: React.ReactNode; empty: string; data: any[];
  }

  const sections: SectionDef[] = [
    {
      key: 'expenses', label: t('Expenses') as string, icon: faReceipt, color: '#dc2626', bg: 'rgba(239,68,68,.12)',
      count: expenses.length, totalLabel: t('Total') as string, data: expenses, empty: t('NoExpenses') as string,
      totalNode: <span style={{ color: '#dc2626' }}>-{money(sumBy(expenses, 'amount'))}</span>,
      body: expenses.map((e) => txRow(e, '#dc2626', '-', 'amount')),
    },
    {
      key: 'income', label: t('Income') as string, icon: faCoins, color: '#059669', bg: 'rgba(5,150,105,.15)',
      count: income.length, totalLabel: t('Incoming') as string, data: income, empty: t('NoData') as string,
      totalNode: <span style={{ color: '#059669' }}>+{money(sumBy(income, 'amount'))}</span>,
      body: income.map((e) => txRow(e, '#059669', '+', 'amount')),
    },
    {
      key: 'car', label: (t('CarExpenses') as string) || 'Car Expenses', icon: faCar, color: '#d97706', bg: 'rgba(217,119,6,.14)',
      count: car.length, totalLabel: t('Total') as string, data: car, empty: t('NoCarExpensesAvailable') as string,
      totalNode: <span style={{ color: '#d97706' }}>-{money(sumBy(car, 'cost'))}</span>,
      body: car.map((e) => txRow(e, '#d97706', '-', 'cost', 'purpose')),
    },
    {
      key: 'payroll', label: t('Payroll') as string, icon: faUser, color: '#7c3aed', bg: 'rgba(124,58,237,.15)',
      count: payroll.length, totalLabel: t('Employees') as string, data: payroll, empty: t('NoData') as string,
      totalNode: <span style={{ color: '#7c3aed' }}>{payroll.length}</span>,
      body: payroll.map(payRow),
    },
  ];

  return (
    <div className="flex flex-col gap-[10px]">
      {/* Компактная сводка */}
      <div className="m-fin-sum">
        <div>
          <div className="k">{t('Balance')}</div>
          <div className="v" style={{ fontSize: 20, color: stats.balance >= 0 ? 'var(--text-color)' : '#dc2626' }}>{money(stats.balance)}</div>
        </div>
        <div className="io">
          <div>
            <div className="k">{t('Incoming')}</div>
            <div className="v" style={{ fontSize: 14, color: '#059669' }}>{money(stats.incoming)}</div>
          </div>
          <div>
            <div className="k">{t('Outgoing')}</div>
            <div className="v" style={{ fontSize: 14, color: '#dc2626' }}>{money(stats.outgoing)}</div>
          </div>
        </div>
      </div>

      {/* Секции-аккордеоны */}
      <div className="m-acc">
        {sections.map((s) => {
          const isOpen = !!open[s.key];
          return (
            <div key={s.key} className="m-acc-item">
              <button type="button" className="m-acc-head" onClick={() => toggle(s.key)} aria-expanded={isOpen}>
                <span className="m-acc-ic" style={{ background: s.bg, color: s.color }}><FontAwesomeIcon icon={s.icon} /></span>
                <span className="m-acc-main">
                  <span className="m-acc-title">{s.label}</span>
                  <span className="m-acc-sub">{s.count} {s.count === 1 ? t('Item') : t('Items')}</span>
                </span>
                <span className="m-acc-tot">{s.totalNode}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`m-acc-chev${isOpen ? ' open' : ''}`} />
              </button>
              {isOpen && (
                <div className="m-acc-body">
                  {s.data.length === 0 ? <div className="m-acc-empty">{s.empty}</div> : s.body}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileFinance;
